import React from "react";
import { RateCard, AssetRateCard } from "../types";
import { formatCurrency } from "../utils";
import { DEFAULT_RATES, DEFAULT_ASSET_RATES } from "../constants";
import { RotateCcw, Info } from "lucide-react";

interface Props {
  rates: RateCard;
  assetRates: AssetRateCard;
  currency: string;
  onChange: (rates: RateCard) => void;
  onAssetRatesChange: (rates: AssetRateCard) => void;
  readOnly?: boolean;
  hideRestoreDefaults?: boolean;
  globalDefaultRates?: RateCard;
  globalDefaultAssetRates?: AssetRateCard;
  showOnly?: "shots" | "assets" | "misc" | "all";
}

export function RateCardForm({ 
  rates, 
  assetRates, 
  currency, 
  onChange, 
  onAssetRatesChange, 
  readOnly = false,
  hideRestoreDefaults = false,
  globalDefaultRates,
  globalDefaultAssetRates,
  showOnly = "all"
}: Props) {
  const defRates = globalDefaultRates || DEFAULT_RATES;
  const defAssetRates = globalDefaultAssetRates || DEFAULT_ASSET_RATES;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const { name, value } = e.target;
    const numValue = parseFloat(value);
    onChange({
      ...rates,
      [name]: isNaN(numValue) || numValue < 0 ? 0 : numValue,
    });
  };

  const handleAssetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const { name, value } = e.target;
    const numValue = parseFloat(value);
    onAssetRatesChange({
      ...assetRates,
      [name]: isNaN(numValue) || numValue < 0 ? 0 : numValue,
    });
  };

  const tasks = [
    { key: "supervisor", label: "Supervision" },
    { key: "concept", label: "Concept" },
    { key: "dmp", label: "DMP" },
    { key: "comp", label: "Comp" },
    { key: "roto", label: "Roto" },
    { key: "prep", label: "Prep" },
    { key: "gfx", label: "GFX" },
    { key: "cameraTR", label: "CameraTR" },
    { key: "objectTR", label: "ObjectTR" },
    { key: "rANIM", label: "rANIM" },
    { key: "layout", label: "Layout" },
    { key: "anim", label: "Anim" },
    { key: "light", label: "Light" },
    { key: "fx", label: "FX" },
  ];

  const assetTasks = [
    { key: "supervisor", label: "Supervision" },
    { key: "concept", label: "Concept" },
    { key: "dmp", label: "DMP" },
    { key: "model", label: "Model" },
    { key: "rig", label: "Rig" },
    { key: "retop", label: "Retop" },
    { key: "cloth", label: "Cloth" },
    { key: "fur", label: "Fur" },
    { key: "groom", label: "Groom" },
    { key: "texture", label: "Texture" },
    { key: "anim", label: "Anim" },
    { key: "fx", label: "FX" },
    { key: "lookdev", label: "Lookdev" },
    { key: "photoscan", label: "Photoscan" },
  ];

  return (
    <div className="space-y-3">
      {(showOnly === "all" || showOnly === "shots") && (
        <div className={showOnly === "all" ? "mb-8" : ""}>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider">Shot Rates</h3>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Define standard rates and parameters for shot-based bidding</p>
            </div>
            {!readOnly && !hideRestoreDefaults && (
              <button
                onClick={() => onChange(defRates)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold uppercase tracking-wider rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
              >
                <RotateCcw className="w-3 h-3" />
                Restore Defaults
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
            {tasks.map((task) => (
              <div key={task.key}>
                <label className="mb-1 block truncate text-[10px] font-medium uppercase tracking-wider text-slate-500" title={task.label}>{task.label}</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                    <span className="text-slate-400 text-xs">
                      {formatCurrency(0, currency).replace(/[0-9.,]/g, "").trim()}
                    </span>
                  </div>
                  <input
                    type="number"
                    name={task.key}
                    value={rates[task.key as keyof RateCard] || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                    min="0"
                    step="10"
                    className="h-10 w-full rounded-lg border border-slate-300 pl-6 pr-2 text-xs focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(showOnly === "all" || showOnly === "assets") && (
        <div className={showOnly === "all" ? "mb-8" : ""}>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider">Asset Rates</h3>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Define standard rates and parameters for asset-based bidding</p>
            </div>
            {!readOnly && !hideRestoreDefaults && (
              <button
                onClick={() => onAssetRatesChange(defAssetRates)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold uppercase tracking-wider rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
              >
                <RotateCcw className="w-3 h-3" />
                Restore Defaults
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
            {assetTasks.map((task) => (
              <div key={task.key}>
                <label className="mb-1 block truncate text-[10px] font-medium uppercase tracking-wider text-slate-500" title={task.label}>{task.label}</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                    <span className="text-slate-400 text-xs">
                      {formatCurrency(0, currency).replace(/[0-9.,]/g, "").trim()}
                    </span>
                  </div>
                  <input
                    type="number"
                    name={task.key}
                    value={assetRates[task.key as keyof AssetRateCard] || ""}
                    onChange={handleAssetChange}
                    disabled={readOnly}
                    min="0"
                    step="10"
                    className="h-10 w-full rounded-lg border border-slate-300 pl-6 pr-2 text-xs focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(showOnly === "all" || showOnly === "misc") && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wider">Miscellaneous Rates</h3>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Define rates for onset supervision and other services</p>
            </div>
            {!readOnly && !hideRestoreDefaults && (
              <button
                onClick={() => onChange({ ...rates, onsetSupervision: defRates.onsetSupervision })}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-[10px] font-bold uppercase tracking-wider rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
              >
                <RotateCcw className="w-3 h-3" />
                Restore Defaults
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="mb-1 block truncate text-[10px] font-medium uppercase tracking-wider text-slate-500" title="Onset Supervision">Onset Supervision</label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                  <span className="text-slate-400 text-xs">
                    {formatCurrency(0, currency).replace(/[0-9.,]/g, "").trim()}
                  </span>
                </div>
                <input
                  type="number"
                  name="onsetSupervision"
                  value={rates.onsetSupervision ?? 600}
                  onChange={handleChange}
                  disabled={readOnly}
                  min="0"
                  step="10"
                  className="h-10 w-full rounded-lg border border-slate-300 pl-6 pr-2 text-xs focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
